import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Zap, Boxes, Search, ChevronRight } from 'lucide-react';
import { TRIGGER_TYPES, ACTION_TYPES } from './nodeTypes';

function NodeOption({ nodeType, onSelect }) {
  const Icon = nodeType.icon;

  const onDragStart = (event) => {
    event.dataTransfer.setData('application/reactflow', nodeType.type);
    event.dataTransfer.effectAllowed = 'move';
  };

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onClick={() => onSelect(nodeType.type)}
      className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
    >
      <div
        className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: `${nodeType.color}10` }}
      >
        <Icon className="w-3 h-3" style={{ color: nodeType.color }} strokeWidth={2} />
      </div>
      <span className="text-[12px] font-medium text-gray-700 truncate">
        {nodeType.label}
      </span>
    </div>
  );
}

export default function CanvasContextMenu({ x, y, onAddNode, onClose, connectFrom }) {
  // When opened from edge drop, skip straight to actions
  const isEdgeDrop = !!connectFrom;
  const [expandedCategory, setExpandedCategory] = useState(isEdgeDrop ? 'action' : null);
  const [search, setSearch] = useState('');
  const menuRef = useRef(null);
  const searchRef = useRef(null);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        if (expandedCategory && !isEdgeDrop) {
          setExpandedCategory(null);
          setSearch('');
        } else {
          onClose();
        }
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose, expandedCategory, isEdgeDrop]);

  // Close on click outside
  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose();
      }
    };
    // Use timeout to avoid closing immediately from the same right-click / mouseup
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClick);
    }, 50);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [onClose]);

  // Focus search when category expands
  useEffect(() => {
    if (expandedCategory && searchRef.current) {
      requestAnimationFrame(() => searchRef.current?.focus());
    }
  }, [expandedCategory]);

  const nodes = expandedCategory === 'trigger' ? TRIGGER_TYPES : ACTION_TYPES;
  const query = search.toLowerCase();

  const filteredGroups = useMemo(() => {
    if (!expandedCategory) return [];
    const filtered = nodes.filter((n) =>
      !query || n.label.toLowerCase().includes(query) || (n.subcategory || '').toLowerCase().includes(query)
    );

    if (expandedCategory === 'trigger') {
      return [{ name: null, items: filtered }];
    }

    const groups = {};
    for (const node of filtered) {
      const sub = node.subcategory || 'Other';
      if (!groups[sub]) groups[sub] = [];
      groups[sub].push(node);
    }

    const order = ['CRM', 'AI', 'Prospecting', 'Communication', 'Web', 'Logic', 'Other'];
    return order
      .filter((name) => groups[name])
      .map((name) => ({ name, items: groups[name] }));
  }, [nodes, query, expandedCategory]);

  // Clamp position so menu doesn't go off-screen
  const menuStyle = {
    position: 'fixed',
    top: Math.min(y, window.innerHeight - 120),
    left: Math.min(x, window.innerWidth - 200),
    zIndex: 60,
  };

  return createPortal(
    <div ref={menuRef} style={menuStyle} className="flex gap-1">
      {/* Main menu */}
      {!expandedCategory && (
        <div className="w-44 bg-white rounded-xl shadow-lg border border-gray-100 py-1 animate-in fade-in duration-100">
          <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
            Add Node
          </div>
          <button
            onClick={() => setExpandedCategory('trigger')}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <Zap className="w-3.5 h-3.5 text-gray-400" strokeWidth={2} />
            <span className="flex-1 text-left font-medium">Trigger</span>
            <ChevronRight className="w-3 h-3 text-gray-300" />
          </button>
          <button
            onClick={() => setExpandedCategory('action')}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <Boxes className="w-3.5 h-3.5 text-gray-400" strokeWidth={2} />
            <span className="flex-1 text-left font-medium">Action</span>
            <ChevronRight className="w-3 h-3 text-gray-300" />
          </button>
        </div>
      )}

      {/* Expanded node picker */}
      {expandedCategory && (
        <div className="w-56 max-h-[60vh] bg-white rounded-xl shadow-lg border border-gray-100 flex flex-col overflow-hidden">
          {/* Header with back */}
          <div className="flex items-center gap-2 px-3 pt-2.5 pb-1.5">
            {!isEdgeDrop && (
              <button
                onClick={() => { setExpandedCategory(null); setSearch(''); }}
                className="p-0.5 rounded text-gray-400 hover:text-gray-600 transition-colors"
              >
                <ChevronRight className="w-3.5 h-3.5 rotate-180" />
              </button>
            )}
            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
              {isEdgeDrop ? 'Add Node' : expandedCategory === 'trigger' ? 'Triggers' : 'Actions'}
            </span>
          </div>

          {/* Search */}
          <div className="px-3 pb-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-300" />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                className="w-full pl-8 pr-3 py-1.5 text-[12px] border border-gray-100 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-300 focus:border-gray-300 placeholder:text-gray-300 transition-colors bg-gray-50/50"
              />
            </div>
          </div>

          {/* Node list */}
          <div className="flex-1 overflow-y-auto px-2 pb-2">
            {filteredGroups.length === 0 && (
              <p className="text-[12px] text-gray-400 text-center py-4">No nodes found</p>
            )}
            {filteredGroups.map((group) => (
              <div key={group.name || '_flat'} className="mb-2 last:mb-0">
                {group.name && (
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1 px-2.5 pt-1">
                    {group.name}
                  </div>
                )}
                <div className="flex flex-col gap-0.5">
                  {group.items.map((nodeType) => (
                    <NodeOption
                      key={nodeType.type}
                      nodeType={nodeType}
                      onSelect={onAddNode}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
